import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Image as ImageIcon, Upload, Loader2, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export function CompanyLogoUpload() {
  const { user, orgId } = useAuth();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    void loadLogo();
  }, [orgId]);

  const loadLogo = async () => {
    if (!orgId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("organizations")
      .select("logo_url")
      .eq("id", orgId)
      .single();
    if (!error && data) setLogoUrl(data.logo_url);
    setLoading(false);
  };

  const handleFile = async (file: File) => {
    if (!orgId || !user) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/svg+xml"].includes(file.type)) {
      toast.error("Logo must be PNG, JPG or SVG");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be smaller than 2MB");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `${orgId}/logo-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("company-logos")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("company-logos").getPublicUrl(path);
      const url = pub.publicUrl;
      const { error: updErr } = await supabase
        .from("organizations")
        .update({ logo_url: url })
        .eq("id", orgId);
      if (updErr) throw updErr;
      setLogoUrl(url);
      toast.success("Logo updated");
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!orgId) return;
    setUploading(true);
    try {
      const { error } = await supabase
        .from("organizations")
        .update({ logo_url: null })
        .eq("id", orgId);
      if (error) throw error;
      setLogoUrl(null);
      toast.success("Logo removed");
    } catch (e: any) {
      toast.error(e.message || "Failed to remove logo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="w-5 h-5" />
          Company Logo
        </CardTitle>
        <CardDescription>
          Used on proposals, agreements, invoices and customer-facing emails. PNG, JPG or SVG, max 2MB.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex items-center gap-6">
            <div className="w-32 h-32 rounded-lg border bg-muted flex items-center justify-center overflow-hidden">
              {logoUrl ? (
                <img src={logoUrl} alt="Company logo" className="w-full h-full object-contain" />
              ) : (
                <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="logo-input" className="block text-sm font-medium">
                {logoUrl ? "Replace logo" : "Upload logo"}
              </Label>
              <div className="flex gap-2">
                <Button asChild variant="outline" disabled={uploading}>
                  <label htmlFor="logo-input" className="cursor-pointer">
                    {uploading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Choose file
                    <input
                      id="logo-input"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleFile(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </Button>
                {logoUrl && (
                  <Button variant="ghost" onClick={handleRemove} disabled={uploading}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Square or wide formats both work.</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
